import {
    Availability,
    AvailabilityStatus,
    type DateRange,
    type Employee,
    type EmployeeSortProperty,
    EmployeeStatus,
    GetAvailabilitiesParams,
    GetRosterNotesParams,
    GetSchoolHolidaysParams,
    type Holiday,
    type RosterNoteDTO,
    type SchoolHoliday,
    type TimeEntry,
    TimeEntryType,
    createRosterNote,
    dateAdd,
    dateSub,
    employeeNameOrderForSort,
    formatDateShort,
    formatWeekDayShort,
    getEmployeeName,
    getHolidayForDate,
    getHolidaysInRange,
    getRange,
    matchesFilters,
    monthNames,
    parseDateString,
    timeEntryTypeColor,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import anime from "animejs";
import { LitElement, css, html } from "lit";
import { customElement, property, query, queryAll, state } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import "../../../components/roster-notes/roster-note-list";
import "../../../components/roster-notes/roster-note-popover";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/availability-dialog";
import type { AvailabilityDialog } from "../../../elements/availability-dialog";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/month-picker";
import type { MonthPicker } from "../../../elements/month-picker";
import "../../../elements/popover";
import type { Popover } from "../../../elements/popover";
import { app, router } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../employees-filter";

interface Month {
    name: string;
    from: string;
    to: string;
    year: number;
    days: {
        date: DateString;
        holiday: Holiday | null;
        dayOfWeek: number;
    }[];
    isVisible: boolean;
}

type AvailabilityInputEvent = CustomEvent<{ employee: Employee; date: DateString; dragEvent: DragEvent }>;

@customElement("ptc-employee-availabilities-row")
export class EmployeeAvailabilitysRow extends LitElement {
    @property({ attribute: false })
    employee: Employee;

    @property({ attribute: false })
    availabilities: Availability[];

    @property({ attribute: false })
    months: Month[];

    @property({ attribute: false })
    from: DateString;

    @property({ attribute: false })
    to: DateString;

    @property({ type: Boolean })
    isVisible = false;

    shouldUpdate(changes: Map<string, any>) {
        return changes.has("isVisible") || this.isVisible;
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return html`${cache(this.isVisible ? this._render() : "")}`;
    }

    // protected async performUpdate() {
    //     await new Promise<void>((resolve) => setTimeout(resolve));
    //     super.performUpdate();
    // }

    private async _dragstart(dragEvent: DragEvent, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("drag-start", { detail: { dragEvent, employee, date } }));
    }

    private _dragover(e: DragEvent) {
        e.preventDefault();
        e.dataTransfer!.dropEffect = "copy";
    }

    private _dragenter(dragEvent: DragEvent, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("drag-enter", { detail: { dragEvent, employee, date } }));
    }

    private async _dragend() {
        this.dispatchEvent(new CustomEvent("drag-end"));
    }

    private _drop() {}

    private async _dayClicked(dragEvent: Event, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("day-clicked", { detail: { dragEvent, employee, date } }));
    }

    private async _editAvailability(availability: Availability, e?: Event) {
        e?.stopPropagation();
        this.dispatchEvent(
            new CustomEvent("edit-availability", {
                detail: { availability },
            })
        );
    }

    private _render() {
        const employee = this.employee;
        const availabilities = this.availabilities.filter((av) => av.employeeId === employee.id);

        return html`
            <div
                class="row-header horizontal spacing center-aligning layout click"
                @click=${() => router.go(`employees/${employee.id}/availabilities`)}
            >
                <ptc-ui-avatar
                    .employee=${employee}
                    .positionColors=${employee.positions.map((p) => app.company.getPositionColor(p))}
                ></ptc-ui-avatar>
                <div class="stretch ellipsis right-padded" style="font-size: 0.9em;">
                    ${getEmployeeName(employee, employeeNameOrderForSort(app.employeeFilters.sortProperty))}
                </div>
            </div>
            ${this.months.map(
                ({ days, isVisible }) =>
                    html`<div class="month horizontal layout" style="width: calc(${days.length} * var(--cell-size));">
                        ${cache(
                            isVisible
                                ? html`
                                      ${days.map(({ date, dayOfWeek, holiday }) => {
                                          const avs = availabilities.filter((e) => e.date === date);
                                          const contract = employee.getContractForDate(date);
                                          const blocked =
                                              (!contract || contract.blocked) && (contract?.comment || "inaktiv");

                                          return blocked
                                              ? html`
                                                    <div class="centering layout day relative">
                                                        <ptc-ui-icon
                                                            name="icon.status.blocked"
                                                            class="faded"
                                                            title="${blocked}"
                                                        ></ptc-ui-icon>
                                                    </div>
                                                `
                                              : html`
                                                    <div
                                                        class="click day ${holiday
                                                            ? "violet"
                                                            : dayOfWeek === 0
                                                              ? "orange"
                                                              : ""} relative"
                                                        title=${holiday
                                                            ? holiday.name
                                                            : `${formatWeekDayShort(date)}, ${formatDateShort(date)}`}
                                                        @click=${(e: Event) => this._dayClicked(e, employee, date)}
                                                        draggable="false"
                                                        @dragstart=${(e: DragEvent) =>
                                                            this._dragstart(e, employee, date)}
                                                        @dragenter=${(e: DragEvent) =>
                                                            this._dragenter(e, employee, dateAdd(date, { days: 1 }))}
                                                        @dragover=${this._dragover}
                                                        @dragend=${this._dragend}
                                                        @drop=${this._drop}
                                                    >
                                                        ${avs[0]
                                                            ? html`
                                                                  <div
                                                                      class="fullbleed centering layout availability-icon ${avs[0]
                                                                          .start || avs[0].end
                                                                          ? "cutoff-bottomright"
                                                                          : ""}"
                                                                      style="--color-highlight: ${avs[0].color}"
                                                                      title="${avs[0].fullLabel}"
                                                                      @click=${(e: Event) =>
                                                                          this._editAvailability(avs[0], e)}
                                                                  >
                                                                      <ptc-ui-icon name="${avs[0].icon}"></ptc-ui-icon>
                                                                  </div>
                                                              `
                                                            : ""}
                                                        ${avs[1]
                                                            ? html`
                                                                  <div
                                                                      class="fullbleed centering layout availability-icon cutoff-topleft"
                                                                      style="--color-highlight: ${avs[1].color}"
                                                                      title="${avs[1].fullLabel}"
                                                                      @click=${(e: Event) =>
                                                                          this._editAvailability(avs[1], e)}
                                                                  >
                                                                      <ptc-ui-icon name="${avs[1].icon}"></ptc-ui-icon>
                                                                  </div>
                                                              `
                                                            : ""}
                                                    </div>
                                                `;
                                      })}
                                  `
                                : ""
                        )}
                    </div>`
            )}
        `;
    }
}

@customElement("ptc-employees-availabilities-all")
export class EmployeesAvailabilitiesAll extends Routing(StateMixin(LitElement)) {
    routePattern = /employees\/all\/availabilities/;

    get routeTitle() {
        return "Verfügbarkeiten: Alle Mitarbeiter";
    }

    private get _employees() {
        return app.getFilteredEmployees({ from: this._from, to: this._to });
    }

    private get _daysCount() {
        return dateSub(this._from, this._to);
    }

    private get _filteredRosterNotes() {
        const filteredEmployees = this._employees.filter(
            (employee) => employee.status !== EmployeeStatus.Probation && app.employeeMatchesFilters(employee)
        );
        return this._rosterNotes.filter((note) =>
            filteredEmployees.some((employee) => matchesFilters(note.filters, { company: app.company, employee }))
        );
    }

    @state()
    private _loading = false;

    @state()
    private _from: DateString;

    @state()
    private _to: DateString;

    @state()
    private _months: Month[] = [];

    @state()
    private _availabilities: Availability[] = [];

    @state()
    private _entries: TimeEntry[] = [];

    @state()
    private _rosterNotes: RosterNoteDTO[] = [];

    @state()
    private _schoolHolidays: SchoolHoliday[] = [];

    @singleton("ptc-availability-dialog")
    private _availabilityDialog: AvailabilityDialog;

    private _newAvailabilityData: {
        employee: Employee;
        start: DateString;
        end: DateString;
        length: number;
        top: number;
        left: number;
        right: number;
    } | null = null;

    private _monthsIntersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => this._monthsIntersectionHandler(entries),
        { root: this, rootMargin: "0px 0px 0px -200px" }
    );

    @query(".scrolling", true)
    private _scroller: HTMLDivElement;

    @query(".availability.dragging", true)
    private _dragElement: HTMLDivElement;

    @query("#dragImage", true)
    private _dragImage: HTMLDivElement;

    @query("ptc-month-picker", true)
    private _monthPicker: MonthPicker;

    @queryAll(".header-row .month")
    private _monthHeaders: HTMLDivElement[];

    @query("#newAvailabilityPopover")
    private _newAvailabilityPopover: Popover;

    async handleRoute() {
        if (!this.date) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }

        const months = this._setRange(getRange(this.date, "year"));

        // Load schedule in the background
        void this.updateMonths(months);

        // already scroll to new date as soon as rerender is complete
        await this.updateComplete;
        void this._scrollToDate(this.date);
    }

    private _scrollAnimation: any | null = null;
    private async _scrollToDate(date: string) {
        const el = this.renderRoot!.querySelector(`#header-${date}`) as HTMLElement;
        const rowHeader = this.renderRoot!.querySelector(".header-row .row-header") as HTMLElement;

        if (this._scrollAnimation) {
            this._scrollAnimation.remove(this._scroller);
        }

        this._monthsIntersectionObserver.disconnect();

        this._scrollAnimation = anime({
            targets: this._scroller,
            scrollLeft: el.offsetLeft - rowHeader.offsetWidth,
            duration: 500,
            easing: "easeInOutCubic",
        });

        await this._scrollAnimation.finished;

        this._scrollAnimation = null;

        this._monthHeaders.forEach((e) => this._monthsIntersectionObserver.observe(e));
    }

    private _setRange({ from, to }: DateRange): Month[] | undefined {
        if (from === this._from && to === this._to) {
            return;
        }
        this._from = from;
        this._to = to;
        const days: string[] = [];
        const months: Month[] = [];
        let month: Month | undefined = undefined;
        let date = this._from;

        while (date < this._to) {
            days.push(date);
            const monthRange = getRange(date, "month");
            if (!month || month.from !== monthRange.from) {
                const d = parseDateString(monthRange.from)!;
                const name = monthNames[d.getMonth()];
                month = {
                    from: monthRange.from,
                    to: monthRange.to,
                    name,
                    year: d.getFullYear(),
                    days: [],
                    isVisible: false,
                };
                months.push(month);
            }
            const holiday = getHolidayForDate(date, {
                holidays: app.venues[0]?.enabledHolidays,
                country: app.company!.country,
            });
            month.days.push({ date, holiday, dayOfWeek: new Date(date).getDay() });
            date = dateAdd(date, { days: 1 });
        }

        return months;
    }

    private async updateMonths(months: Month[] | undefined) {
        if (!months) return;

        this._monthsIntersectionObserver.disconnect();
        this._months = months;

        await this._load();

        await this.updateComplete;
        this._monthHeaders.forEach((e) => this._monthsIntersectionObserver.observe(e));
    }

    private _monthsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        for (const entry of entries) {
            const el = entry.target as HTMLElement;
            const month = this._months.find((m) => m.from === el.dataset.date);
            if (month) {
                month.isVisible = entry.isIntersecting;
            }
        }
        const currMonth = this._months.find((m) => m.isVisible)?.from;
        if (currMonth) {
            this._monthPicker.date = currMonth;
            this._monthHeaders.forEach((el) => el.classList.toggle("current-month", el.dataset.date === currMonth));
        }
        this._months = [...this._months];
    }

    private _rowsInteractionObserver?: IntersectionObserver;

    private _rowsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        entries.forEach((e) => ((e.target as EmployeeAvailabilitysRow).isVisible = e.isIntersecting));
    }

    private _observeRows() {
        if (!this._rowsInteractionObserver) {
            const scroller = this.renderRoot.querySelector(".scrolling");
            if (!scroller) {
                return;
            }
            this._rowsInteractionObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => this._rowsIntersectionHandler(entries),
                { root: scroller, rootMargin: "100%" }
            );
        }
        const elements = this.renderRoot.querySelectorAll("ptc-employee-availabilities-row");
        for (const el of elements) {
            this._rowsInteractionObserver.observe(el);
        }
    }

    updated() {
        this._observeRows();
    }

    private async _loadRosterNotes(showSpinner = false) {
        if (showSpinner) {
            this._loading = true;
        }

        try {
            this._rosterNotes = await app.api.getRosterNotes(
                new GetRosterNotesParams({
                    from: this._from,
                    to: this._to,
                })
            );
        } catch (e) {
            console.error(e);
        }

        if (showSpinner) {
            this._loading = false;
        }
    }

    private async _load() {
        this._loading = true;

        try {
            const [availabilities, schoolHolidays] = await Promise.all([
                app.api.getAvailabilities(
                    new GetAvailabilitiesParams({
                        from: this._from,
                        to: this._to,
                        employees: this._employees.map((e) => e.id),
                    })
                ),
                app.api.getSchoolHolidays(
                    new GetSchoolHolidaysParams({
                        from: this._from,
                        to: this._to,
                        country: app.company.country,
                        states: app.venues.map((venue) => venue.state),
                    })
                ),
                this._loadRosterNotes(),
            ]);

            this._availabilities = availabilities;
            this._schoolHolidays = schoolHolidays;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _dayClicked({ detail: { employee, date } }: AvailabilityInputEvent) {
        const created = await this._availabilityDialog.show(
            new Availability({
                employeeId: employee.id,
                status: AvailabilityStatus.Available,
                date,
            })
        );
        if (created) {
            // update availabilities in the background
            void this._load();
        }
    }

    private async _openNewAvailabilityPopover(ignoreFirstClick = false) {
        if (this._newAvailabilityData) {
            await this._newAvailabilityPopover.showAt(this._dragElement, ignoreFirstClick);
            // this._newAvailability(this._newAvailabilityData.employee, this._newAvailabilityData.start, this._newAvailabilityData.end);
        }
        this._dragElement.classList.add("invisible");
        this._newAvailabilityData = null;
    }

    private async _newAvailability() {
        if (!this._newAvailabilityData) {
            return;
        }

        const created = await this._availabilityDialog.show(
            new Availability({
                employeeId: this._newAvailabilityData.employee.id,
                start: this._newAvailabilityData.start,
                end: this._newAvailabilityData.end,
            })
        );
        if (created) {
            // update availabilities in the background
            void this._load();
        }
    }

    private async _editAvailability(availability: Availability) {
        const edited = await this._availabilityDialog.show(availability);
        if (edited) {
            // update availabilities in the background
            void this._load();
        }
    }

    private _updateDragElement() {
        const data = this._newAvailabilityData;
        const el = this._dragElement;
        if (!data || !el) {
            return;
        }
        el.classList.remove("invisible");
        el.style.top = `${data.top}px`;
        el.style.left = `${data.left}px`;
        el.style.width = `${data.right - data.left}px`;
        (el.querySelector(".range") as HTMLDivElement).innerText = `${formatDateShort(data.start)} - ${formatDateShort(
            dateAdd(data.end, { days: -1 })
        )}`;
        (el.querySelector(".length") as HTMLDivElement).innerText = `${data.length} T`;
    }

    private _dragStart({ detail: { dragEvent, employee, date } }: AvailabilityInputEvent) {
        const el = dragEvent.target as HTMLElement;
        dragEvent.dataTransfer!.setDragImage(this._dragImage, 0, 0);
        dragEvent.dataTransfer!.effectAllowed = "copy";
        dragEvent.dataTransfer!.dropEffect = "copy";
        dragEvent.dataTransfer!.setData("text/plain", "blah");
        this._newAvailabilityData = {
            employee,
            start: date,
            end: dateAdd(date, { days: 1 }),
            length: 1,
            top: el.offsetTop,
            left: el.offsetLeft,
            right: el.offsetLeft + el.offsetWidth,
        };

        this._updateDragElement();
    }

    private _dragOver({ detail: { dragEvent } }: AvailabilityInputEvent) {
        dragEvent.preventDefault();
        dragEvent.dataTransfer!.dropEffect = "copy";
    }

    private _dragEnter({ detail: { dragEvent, date } }: AvailabilityInputEvent) {
        if (this._newAvailabilityData) {
            const el = dragEvent.target as HTMLElement;
            this._newAvailabilityData.end =
                date > this._newAvailabilityData.start ? date : dateAdd(this._newAvailabilityData.start, { days: 1 });
            this._newAvailabilityData.length = dateSub(this._newAvailabilityData.start, this._newAvailabilityData.end);
            this._newAvailabilityData.right = el.offsetLeft + el.offsetWidth;
            this._updateDragElement();
        }
    }

    private _dragEnd() {
        void this._openNewAvailabilityPopover();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                --cell-size: 2em;
            }

            .scroller-inner {
                padding-right: calc(100% - 7 * var(--cell-size));
            }

            .row {
                border-bottom: solid 1px var(--shade-2);
                box-sizing: border-box;
                height: 2em;
                contain: size style;
            }

            .header-row {
                background: var(--color-bg);
                position: sticky;
                top: 0;
                z-index: 3;
                border-bottom: solid 1px var(--shade-2);
            }

            .roster-notes-row {
                top: 5.2em;
            }

            .month {
                box-shadow: inset var(--shade-2) -1px 0 0 0px;
                box-sizing: border-box;
                scroll-snap-align: start;
            }

            .month-name {
                padding: 0.9em 1em;
                font-weight: 600;
                transition: all 0.5s;
            }

            .month.current-month .month-name {
                transform: translateX(-50%);
                opacity: 0;
            }

            .row-header {
                background: rgba(255, 255, 255, 0.9);
                border-right: solid 1px var(--shade-2);
                width: calc(7 * var(--cell-size));
                position: sticky;
                left: 0;
                box-sizing: border-box;
                z-index: 1;
            }

            .row-header ptc-ui-avatar {
                font-size: 0.5em;
                margin-left: 0.5em;
            }

            .row-header .vacation {
                margin-right: 0.2em;
                width: 4em;
            }

            .day {
                height: var(--cell-size);
                width: var(--cell-size);
                box-sizing: border-box;
                color: var(--color-highlight);
                background: var(--color-highlight-bg);
                padding: 0.25em;
            }

            .day:not(:last-child) {
                border-right: dashed 1px var(--shade-2);
            }

            .availability {
                position: absolute;
                height: var(--cell-size);
                box-sizing: border-box;
            }

            .availability-inner {
                background: var(--color-highlight);
                color: var(--color-bg);
                border-radius: 0.5em;
                position: absolute;
                top: 0.1em;
                left: 0.1em;
                right: 0.1em;
                bottom: 0.1em;
                padding: 0 0.2em;
                overflow: hidden;
                box-sizing: border-box;
                border: solid 1px transparent;
                line-height: 2em;
            }

            .availability.requested .availability-inner {
                border-color: var(--color-highlight);
                background: var(--color-bg);
                color: var(--color-highlight);
            }

            .dragging {
                opacity: 0.7;
                pointer-events: none;
            }

            .today {
                border-radius: 100%;
                background: var(--blue);
                color: var(--color-bg);
            }

            .roster-notes {
                position: sticky;
                top: 5.2em;
                background: var(--color-bg);
                z-index: 2;
            }

            .availability-icon {
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            .availability-icon.cutoff-topleft {
                clip-path: polygon(100% 0, 100% 100%, 0 100%);
            }

            .availability-icon.cutoff-bottomright {
                clip-path: polygon(100% 0, 0 0, 0 100%);
            }
        `,
    ];

    private _renderHeader() {
        const today = toDateString(new Date());
        return html`<div class="horizontal layout header-row">
            <div class="row-header vertical layout"></div>
            ${this._months.map(
                ({ name, days, year, from }) => html`
                    <div class="month vertical start-aligning layout" data-date="${from}">
                        <div class="month-name stretch">${name} ${year}</div>
                        <div class="horizontal layout">
                            ${days.map(({ date, dayOfWeek, holiday }) => {
                                const d = parseDateString(date)!;
                                return html`
                                    <div
                                        class="text-centering day ${holiday
                                            ? "violet"
                                            : dayOfWeek === 0
                                              ? "orange"
                                              : ""}"
                                        title=${holiday
                                            ? holiday.name
                                            : `${formatWeekDayShort(date)}, ${formatDateShort(date)}`}
                                        id="header-${date}"
                                    >
                                        <div class="${date === today ? "today" : ""}">${d.getDate()}</div>
                                    </div>
                                `;
                            })}
                        </div>
                    </div>
                `
            )}
        </div> `;
    }

    private _renderRosterNotes() {
        return html`
            <div class="header-row roster-notes-row horizontal layout">
                <div class="row-header semibold">
                    <div class="center-aligning horizontal layout">
                        <div class="stretch half-padded">
                            <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>
                            Kalendereinträge
                        </div>
                        <button class="smaller skinny transparent right-margined">
                            <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                        </button>
                        <ptc-roster-note-popover
                            .rosterNote=${createRosterNote({ companyId: app.company.id })}
                            @done=${() => this._loadRosterNotes(true)}
                        ></ptc-roster-note-popover>
                    </div>
                </div>
                <ptc-roster-notes
                    style="width: calc(${this._daysCount} * var(--cell-size))"
                    .rosterNotes=${this._filteredRosterNotes}
                    .schoolHolidays=${this._schoolHolidays}
                    .holidays=${getHolidaysInRange(this._from, this._to, {
                        holidays: app.venues.flatMap((venue) => venue.enabledHolidays),
                        country: app.company.country,
                    })}
                    .dateRange=${{ from: this._from, to: this._to }}
                    @roster-notes-updated=${() => this._loadRosterNotes(true)}
                ></ptc-roster-notes>
            </div>
        `;
    }

    render() {
        if (!this._from || !this._to || !this._months) {
            return html`
                <div class="fullbleed centering layout scrim">
                    <ptc-ui-spinner active></ptc-ui-spinner>
                </div>
            `;
        }

        const employees = this._employees;
        const daysCount = dateSub(this._from, this._to);

        return html`
            <div class="fullbleed vertical layout">
                <div class="padded horizontal spacing center-aligning layout border-bottom">
                    <ptc-month-picker
                        @change=${({ detail: { date } }: CustomEvent<{ date: string }>) => this.go(null, { date })}
                    ></ptc-month-picker>
                    <div class="stretch"></div>
                </div>
                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
                ></ptc-employees-filter>
                <div class="scrolling stretch">
                    <div class="scroller-inner" style="width: calc(${daysCount + 7} * var(--cell-size))">
                        ${this._renderHeader()} ${this._renderRosterNotes()}
                        <div class="relative">
                            ${employees.map((employee) => {
                                const filtered =
                                    employee.status === EmployeeStatus.Probation ||
                                    !app.employeeMatchesFilters(employee);
                                return cache(
                                    filtered
                                        ? null
                                        : html`
                                              <ptc-employee-availabilities-row
                                                  class="row horizontal layout"
                                                  .employee=${employee}
                                                  .availabilities=${this._availabilities}
                                                  .entries=${this._entries}
                                                  .months=${this._months}
                                                  .from=${this._from}
                                                  .to=${this._to}
                                                  @day-clicked=${this._dayClicked}
                                                  @edit-availability=${(
                                                      e: CustomEvent<{ availability: Availability }>
                                                  ) => this._editAvailability(e.detail.availability)}
                                                  @drag-start=${this._dragStart}
                                                  @drag-over=${this._dragOver}
                                                  @drag-enter=${this._dragEnter}
                                                  @drag-end=${this._dragEnd}
                                              ></ptc-employee-availabilities-row>
                                          `
                                );
                            })}

                            <div class="grey availability dragging invisible">
                                <div class="availability-inner spacing center-aligning horizontal layout click">
                                    <ptc-ui-icon name="icon.action.help" class="smaller"></ptc-ui-icon>
                                    <div class="stretch ellipsis range"></div>
                                    <div class="subtle semibold length"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ptc-popover id="newAvailabilityPopover" class="larger tooltip" hide-on-click .anchor=${null}>
                <div class="vertical spacing layout">
                    ${[
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.ChildSick,
                        TimeEntryType.SickInKUG,
                    ].map(
                        (type) => html`
                            <button
                                class="slim ghost"
                                @click=${() => this._newAvailability()}
                                style="--color-highlight: ${timeEntryTypeColor(type)}"
                            >
                                <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(type)}"></ptc-ui-icon>
                                ${app.localized.timeEntryTypeLabel({ type })}
                            </button>
                        `
                    )}
                </div>
            </ptc-popover>

            <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner .active=${this._loading}></ptc-ui-spinner>
            </div>
            <img
                id="dragImage"
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
            />
        `;
    }
}
